import type { Coin } from '@/lib/api/transaction/models';

/**
 * Persisted coins snapshot — seeds `CoinsProvider`'s first render so money
 * formats at the coin's own `decimalPlacesShort` instead of the three-decimal
 * dollar default for the ~600ms `/api/coins` takes. The USD rate is deliberately not
 * persisted: a stale rate is wrong money, and consumers already handle
 * `usdRate: null`.
 */
const COINS_CACHE_KEY = 'outpoll_coins_display';

type CachedCoin = Omit<Coin, 'usdRate' | 'usdRateUpdateDatetime'>;

export function readCachedCoins(): Coin[] {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(COINS_CACHE_KEY) ?? 'null') as CachedCoin[];
    if (!Array.isArray(parsed)) return [];
    return parsed.map((coin) => ({ ...coin, usdRate: null, usdRateUpdateDatetime: '' }));
  } catch {
    return [];
  }
}

export function writeCachedCoins(coins: Coin[]): void {
  try {
    const cached: CachedCoin[] = coins.map(
      ({ usdRate: _rate, usdRateUpdateDatetime: _updatedAt, ...rest }) => rest,
    );
    localStorage.setItem(COINS_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // Ignore quota / serialization errors — the next load just isn't pre-seeded.
  }
}
